import type {
  EAnalyticMode,
  IExtraCondition,
  IPageItemsPayload,
  OrderByFields,
} from '~/core/gemxql/types';
import type { IPAListingItem, IPAListingQueryResult } from '../types/page-analytic-v2';
import type { GemxQlQueryResponse, GemxQlQueryVariables } from '~/core/gemxql/graphql';
import type { AnalyticDateFilter } from '~/presentation/ui';
import type { IFilterSortOption } from '~/types/sort';
import type { TemplateType } from '~/types/template';

import {
  EAnalyticSource,
  EComparisonOperator,
  EGroupWithClause,
  EOrderDirectionType,
  EPageField,
  EPageMetric,
} from '~/core/gemxql/types';
import { extractColumnTypes, extractQueryRows, parsePageItems } from '~/core/gemxql/helpers';
import { PA_SORT_METRIC_PAGE_TITLE } from '../constants/pageAnalytic';
import { PA_SEARCH_FIELDS } from '../constants/pageAnalyticV2';
import { LISTING_METRIC_MAP } from '../constants/metricsV2';
import { buildGemXQlQuery } from '~/core/gemxql/builder';
import { TEMPLATE_ASSIGNED } from '~/constants/template';
import { EMetricKey } from '~/types/metric';

export const EMPTY_PAGE_ITEMS: IPageItemsPayload = {
  shopify_page_id: '',
  location_path: '',
  page_type: '',
  page_title: '',
  template_suffix: '',
};
interface IBuildPAQueryVariablesParams {
  mode: EAnalyticMode;
  metrics: string[];
  dimensions: string[];
  dateRange: AnalyticDateFilter;
  orderBy?: OrderByFields;
  searchValue?: string;
  filterPageTypes?: TemplateType[];
  filterPagePaths?: string[];
  locationPath?: string;
  limit?: number;
  offset?: number;
}

export const buildPAOrderBy = (sortSelected?: string[]): OrderByFields | undefined => {
  // page_path is always the last ORDER BY key so ties on the primary sort resolve
  // deterministically — pagination stays stable and the previous-period query can
  // be matched to the same set of paths returned by the current-period query.
  const pagePathOrderBy: OrderByFields = {
    [EPageField.PAGE_PATH]: { field: EPageField.PAGE_PATH, direction: EOrderDirectionType.ASC },
  };

  const sortValue = sortSelected?.[0];
  if (!sortValue) return pagePathOrderBy;

  const [metricKey, rawDirection] = sortValue.trim().split(/\s+/);
  if (!metricKey || !rawDirection) return pagePathOrderBy;

  const isDesc = rawDirection.toUpperCase() === EOrderDirectionType.DESC;
  // page_title uses natural text order (ASC = A-Z), so no inversion needed.
  // Numeric metrics invert because "Highest first" maps to 'asc' value but needs DESC.
  const isTextField = metricKey === EPageField.PAGE_TITLE;
  const direction = isTextField
    ? isDesc
      ? EOrderDirectionType.DESC
      : EOrderDirectionType.ASC
    : isDesc
      ? EOrderDirectionType.ASC
      : EOrderDirectionType.DESC;

  return { [metricKey]: { field: metricKey, direction }, ...pagePathOrderBy };
};

export const buildPAListingSortOptions = (selectedMetrics: EMetricKey[]): IFilterSortOption => {
  const sortOptions = selectedMetrics.flatMap(
    (metric) => LISTING_METRIC_MAP[metric]?.sortOptions || [],
  );
  return [...PA_SORT_METRIC_PAGE_TITLE, ...sortOptions];
};

export const normalizePAListingSortSelected = (
  sortSelected: string[] | undefined,
  selectedMetrics: EMetricKey[],
): string[] | undefined => {
  if (!sortSelected?.length) return sortSelected;

  const sortOptions = buildPAListingSortOptions(selectedMetrics);
  const hasSelectedSortOption = sortOptions.some((option) => option.value === sortSelected[0]);
  if (hasSelectedSortOption) return sortSelected;

  const fallbackSortValue = sortOptions[0]?.value;
  return fallbackSortValue ? [fallbackSortValue] : undefined;
};

const buildPAExtraConditions = ({
  searchValue,
  filterPageTypes,
  filterPagePaths,
  locationPath,
}: Pick<
  IBuildPAQueryVariablesParams,
  'searchValue' | 'filterPageTypes' | 'filterPagePaths' | 'locationPath'
>): IExtraCondition[] => {
  const extraConditions: IExtraCondition[] = [];

  const pageTypes = filterPageTypes?.length ? filterPageTypes : TEMPLATE_ASSIGNED;
  extraConditions.push({
    operator: EComparisonOperator.IN,
    fields: [EPageField.PAGE_TYPE],
    values: pageTypes,
  });
  if (searchValue) {
    extraConditions.push({
      operator: EComparisonOperator.LIKE,
      fields: PA_SEARCH_FIELDS,
      value: searchValue,
    });
  }
  if (filterPagePaths?.length) {
    extraConditions.push({
      operator: EComparisonOperator.IN,
      fields: [EPageField.PAGE_PATH],
      values: filterPagePaths,
    });
  }
  if (locationPath) {
    extraConditions.push({
      operator: EComparisonOperator.EQ,
      fields: [EPageField.PAGE_PATH],
      value: locationPath,
    });
  }
  return extraConditions;
};

export const buildPAQueryVariables = (data: IBuildPAQueryVariablesParams): GemxQlQueryVariables => {
  const {
    mode,
    metrics,
    dimensions,
    dateRange,
    orderBy,
    searchValue,
    filterPageTypes,
    filterPagePaths,
    locationPath,
    limit,
    offset,
  } = data;

  const input = buildGemXQlQuery({
    mode,
    sources: [EAnalyticSource.SESSIONS],
    metrics,
    dimensions,
    groupWithClause: EGroupWithClause.NONE,
    dateRange,
    filters: {},
    extraConditions: buildPAExtraConditions({
      searchValue,
      filterPageTypes,
      filterPagePaths,
      locationPath,
    }),
    orderBy,
    extraOrderByFields: [EPageField.PAGE_TITLE],
    limit,
    offset,
  });
  return { input };
};

export const extractTotalPagesFromResponse = (response?: GemxQlQueryResponse): number => {
  const firstRow = extractQueryRows(response)[0];
  const totalPages = firstRow?.[EPageMetric.PAGE_PATHS];
  return typeof totalPages === 'number' ? totalPages : 0;
};

export const convertRowsToPageAnalytics = (
  response?: GemxQlQueryResponse,
): IPAListingQueryResult => {
  const items: IPAListingItem[] = extractQueryRows(response).map((row) => {
    const { [EPageMetric.PAGE_ITEMS]: rawPageItems, ...rest } = row;
    return {
      ...rest,
      pageItems: parsePageItems(rawPageItems) ?? EMPTY_PAGE_ITEMS,
    };
  });

  return { items, columnTypes: extractColumnTypes(response) };
};

export const extractPagePaths = (items: IPAListingItem[]): string[] => {
  return items.map((item) => item.pageItems.location_path).filter(Boolean);
};

export const hasListingMetricData = (item?: IPAListingItem): boolean => {
  const sessions = item?.[EMetricKey.SESSION];
  return typeof sessions === 'number' && sessions > 0;
};
